import { Injectable, inject, signal, DestroyRef, NgZone } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, fromEvent } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { EMBEDDED_QUERY_PARAM, EMBEDDED_QUERY_VALUE, EMBEDDED_PATH_PREFIX, EMBEDDED_MESSAGE_TYPE, NATIVE_MESSAGE_TYPE } from '@constants';
import {
  EmbeddedConfig,
  EmbeddedOutboundEvent,
  NativeInboundMessage
} from '@shared/interfaces/embedded.interface';
import { isNullOrUndefined } from '@utils/predicates';

const WILDCARD_ORIGIN = '*';

@Injectable({ providedIn: 'root' })
export class EmbeddedService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly zone = inject(NgZone);

  private readonly _isEmbedded = signal(false);
  private readonly _config = signal<EmbeddedConfig | null>(null);
  private readonly _nativeMessages = new Subject<NativeInboundMessage>();

  /** Signal reactivo: true si la app corre en modo embedded */
  readonly isEmbedded = this._isEmbedded.asReadonly();

  /** Configuración recibida desde la app nativa */
  readonly config = this._config.asReadonly();

  /** Stream de mensajes entrantes desde la app nativa */
  readonly nativeMessages$ = this._nativeMessages.asObservable();

  /**
   * Inicializa detección de modo embedded.
   * Llamado desde APP_INITIALIZER.
   */
  initialize(): void {
    const embedded = this.detectEmbedded();
    this._isEmbedded.set(embedded);

    if (embedded) {
      this.listenNativeMessages();
      this.postMessage({ type: EMBEDDED_MESSAGE_TYPE.READY });
    }
  }

  /** Envía evento tipado a la app nativa via postMessage */
  postMessage(event: EmbeddedOutboundEvent): void {
    if (!this._isEmbedded()) {
      return;
    }

    const origin = this._config()?.origin;
    const targetOrigin = isNullOrUndefined(origin) ? WILDCARD_ORIGIN : origin;
    window.parent.postMessage(event, targetOrigin);
  }

  /** Actualiza la configuración (llamado al recibir CONFIGURE desde nativo) */
  updateConfig(config: EmbeddedConfig): void {
    this._config.set(config);
  }

  private detectEmbedded(): boolean {
    if (this.isEmbeddedRoute()) {
      return true;
    }
    const queryParam = this.getQueryParam(EMBEDDED_QUERY_PARAM);
    if (queryParam === EMBEDDED_QUERY_VALUE) {
      return true;
    }
    return this.isInIframe();
  }

  private isEmbeddedRoute(): boolean {
    const hash = window.location.hash;
    return hash.includes(EMBEDDED_PATH_PREFIX);
  }

  private getQueryParam(name: string): string | null {
    const params = new URLSearchParams(window.location.search);
    const fromSearch = params.get(name);
    if (!isNullOrUndefined(fromSearch)) {
      return fromSearch;
    }

    // Hash-based routing: parse after #
    const hash = window.location.hash;
    const queryIndex = hash.indexOf('?');
    if (queryIndex === -1) {
      return null;
    }
    const hashParams = new URLSearchParams(hash.substring(queryIndex));
    return hashParams.get(name);
  }

  private isInIframe(): boolean {
    try {
      return window.self !== window.top;
    } catch {
      return true;
    }
  }

  private listenNativeMessages(): void {
    this.zone.runOutsideAngular(() => {
      fromEvent<MessageEvent>(window, 'message').pipe(
        filter((event) => this.isValidNativeMessage(event)),
        map((event) => this.parseNativeMessage(event)),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe((message) => {
        this.zone.run(() => this.handleNativeMessage(message));
      });
    });
  }

  private isValidNativeMessage(event: MessageEvent): boolean {
    if (isNullOrUndefined(event.data?.type)) {
      return false;
    }

    const configOrigin = this._config()?.origin;
    if (!isNullOrUndefined(configOrigin) && event.origin !== configOrigin) {
      return false;
    }

    return true;
  }

  private parseNativeMessage(event: MessageEvent): NativeInboundMessage {
    return {
      type: event.data.type,
      ...event.data
    };
  }

  private handleNativeMessage(message: NativeInboundMessage): void {
    if (message.type === NATIVE_MESSAGE_TYPE.CONFIGURE) {
      this.updateConfig(message.config);
    }
    this._nativeMessages.next(message);
  }
}
